import React from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Stack, Typography, Box, IconButton, Grid, Avatar, Divider
} from '@mui/material';
import { 
  Close, PersonOutline, CalendarMonthOutlined, 
  FactCheckOutlined, AssignmentOutlined, GestureOutlined 
} from '@mui/icons-material';

const AssessmentDetailsModal = ({ open, handleClose, selectedResponse }) => {
  if (!selectedResponse) return null;

  const formatResponse = (value) => {
    if (typeof value === 'object' && value !== null) {
      return Object.keys(value)
        .filter((key) => value[key] === true)
        .join(', ');
    }
    return String(value || 'No data recorded');
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md" 
      scroll="paper"
      PaperProps={{ 
        sx: { borderRadius: '24px', boxShadow: '0px 24px 48px rgba(0,0,0,0.1)' } 
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: '#F0F5FF', color: '#2E90FA', width: 48, height: 48 }}>
            <FactCheckOutlined />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} color="#101828" sx={{ lineHeight: 1.2 }}>
              Clinical Review Cycle #{selectedResponse.review_cycle}
            </Typography>
            <Typography variant="caption" color="#667085" fontWeight={500}>
              Official Initial Assessment Record
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} sx={{ bgcolor: '#F9FAFB' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, bgcolor: '#FCFCFD', borderTop: '1px solid #EAECF0' }}>
        {/* UK Audit Metadata */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, borderRadius: '16px', border: '1px solid #EAECF0', bgcolor: '#FFFFFF' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PersonOutline sx={{ color: '#667085', fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" color="#667085" fontWeight={700} sx={{ letterSpacing: '0.5px' }}>
                    AUTHORIZED BY
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="#1D2939" sx={{ textTransform: 'capitalize' }}>
                    {selectedResponse.created_by_name}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 2, borderRadius: '16px', border: '1px solid #EAECF0', bgcolor: '#FFFFFF' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CalendarMonthOutlined sx={{ color: '#667085', fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" color="#667085" fontWeight={700} sx={{ letterSpacing: '0.5px' }}>
                    ASSESSMENT DATE
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="#1D2939">
                    {new Date(selectedResponse.created_at).toLocaleString('en-GB', { 
                      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* Section-wise Clinical Observations */}
        {selectedResponse.form_responses?.map((section, sIdx) => (
          <Box key={sIdx} sx={{ mb: 4 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, ml: 0.5 }}>
              <AssignmentOutlined sx={{ fontSize: 18, color: '#2E90FA' }} />
              <Typography variant="subtitle2" fontWeight={800} color="#344054" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {section.section_title}
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              {section.observations?.map((obs, oIdx) => (
                <Box 
                  key={oIdx} 
                  sx={{ 
                    p: 2.5, 
                    borderRadius: '16px', 
                    bgcolor: '#FFFFFF', 
                    border: '1px solid #F2F4F7'
                  }}
                >
                  <Typography variant="caption" fontWeight={700} color="#667085" sx={{ display: 'block', mb: 1 }}>
                    {obs.question_text}
                  </Typography>
                  <Typography variant="body2" color="#1D2939" fontWeight={600}>
                    {formatResponse(obs.response_value)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}

        {/* --- SIGNATURE SECTION --- */}
        {selectedResponse.signature_svg && (
          <Box sx={{ mt: 4, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, ml: 0.5 }}>
              <GestureOutlined sx={{ fontSize: 18, color: '#2E90FA' }} />
              <Typography variant="subtitle2" fontWeight={800} color="#344054" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Digital Authorization
              </Typography>
            </Stack>
            
            <Box sx={{ 
              p: 2, 
              borderRadius: '20px', 
              bgcolor: '#fff', 
              border: '1px solid #EAECF0',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              {/* SVG রেন্ডারিং */}
              <Box 
                dangerouslySetInnerHTML={{ __html: selectedResponse.signature_svg }} 
                sx={{ 
                  width: '100%', 
                  maxWidth: '400px', 
                  maxHeight: '120px',
                  '& svg': { width: '100%', height: 'auto' }
                }} 
              />
              <Divider sx={{ width: '80%', my: 1, borderColor: '#EAECF0' }} />
              <Typography variant="caption" color="#667085" fontWeight={700}>
                E-SIGNED BY {selectedResponse.created_by_name?.toUpperCase()}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: '#FFFFFF', borderTop: '1px solid #EAECF0' }}>
        <Button 
          onClick={handleClose} 
          variant="outlined" 
          sx={{ borderRadius: '12px', px: 4, textTransform: 'none', fontWeight: 700, color: '#344054', borderColor: '#D0D5DD' }}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssessmentDetailsModal;